import React, { useState, useEffect } from 'react';
import { useWorkflowTemplates } from '../hooks/useWorkflowTemplates';
import { useWorkflowEngine } from '../hooks/useWorkflowEngine';

/**
 * StartWorkflowModal - Manual trigger modal to select a template and provide context.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onStarted: (instance) => void
 * - preselectedContext: { studentId, studentName, ... } - pre-fill context from caller
 */
export default function StartWorkflowModal({ isOpen, onClose, onStarted, preselectedContext = {} }) {
  const { allTemplates, loading: templatesLoading } = useWorkflowTemplates();
  const { startWorkflow, starting, error } = useWorkflowEngine();

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [context, setContext] = useState({});

  // Only show active templates
  const activeTemplates = allTemplates.filter(t => t.active);

  // Reset only when modal opens (not on every render)
  const [wasOpen, setWasOpen] = useState(false);
  useEffect(() => {
    if (isOpen && !wasOpen) {
      setSelectedTemplateId('');
      setContext(preselectedContext || {});
    }
    setWasOpen(isOpen);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTemplate = activeTemplates.find(t => t.id === selectedTemplateId);

  // Check which context fields are required
  const requiredFields = selectedTemplate?.contextSchema
    ? Object.entries(selectedTemplate.contextSchema).filter(([, v]) => v.required)
    : [];

  const canStart = selectedTemplateId && !starting &&
    requiredFields.every(([key]) => context[key]);

  const handleStart = async () => {
    if (!canStart) return;

    try {
      const result = await startWorkflow(selectedTemplateId, context);
      onStarted?.(result);
      onClose();
    } catch (err) {
      // Error already set in hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-4">Start a Flow</h3>

        {/* Template selector */}
        <div className="flex flex-col gap-1.5 mb-4">
          <label className="label"><span className="label-text font-medium">Which flow?</span></label>
          {templatesLoading ? (
            <div className="h-10 bg-base-300 rounded animate-pulse" />
          ) : activeTemplates.length === 0 ? (
            <p className="text-sm text-base-content/50">No flows created yet — go to Flows to create one</p>
          ) : (
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="">Choose a template...</option>
              {activeTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.steps?.length || 0} steps)
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Template preview */}
        {selectedTemplate && (
          <div className="bg-base-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-base-content/70 mb-2">{selectedTemplate.description || 'No description'}</p>
            <div className="flex gap-3 text-xs text-base-content/50">
              <span>{selectedTemplate.steps?.length} steps</span>
              <span className="capitalize">{selectedTemplate.category}</span>
            </div>
          </div>
        )}

        {/* Context fields */}
        {selectedTemplate && requiredFields.length > 0 && (
          <div className="space-y-3 mb-4">
            <label className="text-sm font-medium text-base-content/70">Context</label>
            {requiredFields.map(([key, schema]) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label className="label">
                  <span className="label-text">{schema.label || key}</span>
                </label>
                {preselectedContext[key] ? (
                  <div className="input input-bordered flex items-center bg-base-200 text-base-content/70">
                    {preselectedContext[`${key.replace('Id', 'Name')}`] || preselectedContext[key]}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={context[key] || ''}
                    onChange={(e) => setContext(prev => ({ ...prev, [key]: e.target.value }))}
                    className="input input-bordered w-full"
                    placeholder={`Enter ${schema.label || key}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="modal-action">
          <button onClick={onClose} className="btn btn-ghost" disabled={starting}>
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={!canStart}
            className="btn btn-primary"
          >
            {starting ? 'Starting...' : 'Go'}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
}
